import List from "@elements/List/List";
import { ListItem } from "@elements/List/ListItem/ListItem";
import { HeaderMenuDetails } from "@header/HeaderMenuConstants";
import { HeaderMenuType } from "@header/HeaderMenuTypes";
import { SubMenuDropdown } from "@header/HeaderSubMenuDropdown";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { twMerge } from "tailwind-merge";

type HeaderMenuListProps = {
  /**
   * Test ID used to identify the main container.
   */
  testId: string;

  /**
   * Collection of menu items to render.
   */
  items: HeaderMenuDetails[];

  /**
   * Defines the type and layout of the header menu.
   *
   * The menu type controls the responsive styling and behavior
   * applied to the list and its items.
   */
  type: HeaderMenuType;

  /**
   * Checks whether the menu is being rendered as part of
   * the hamburger/mobile navigation.
   *
   * When enabled, the dropdown is initially expanded.
   */
  isHamburger: boolean;

  /**
   * Section name related to page.
   */
  sectionName?: string;

  /**
   * Checks whether the article first-fold design is enabled.
   * @default false
   */
  isArticleFirstFoldEnabled?: boolean;
};

/**
 * Renders a list of header navigation menu items.
 *
 * The component supports multiple header menu types, including
 * main, mobile, social, and top-mobile menus. Menu items can either
 * render as direct navigation links or as expandable dropdown menus
 * when child items are provided.
 *
 * @param props - Configuration options for the header menu.
 * @returns A rendered header navigation list.
 */
export function HeaderMenuList({
  testId,
  items,
  type,
  isHamburger,
  sectionName = "",
  isArticleFirstFoldEnabled = false,
}: HeaderMenuListProps) {
  const [dropdown, setDropdown] = useState(isHamburger);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const toggleDropdown = () => {
    setDropdown((prev) => !prev);
  };

  const closeDropdown = () => {
    setDropdown(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  return (
    <List
      testId={`${testId}-${type}-list`}
      className={twMerge(
        "block",
        type === "social" && "inline-flex gap-9 lg:gap-0 xs:py-5 lg:pt-0",
        type === "main" &&
          "grid gap-5 lg:gap-0 lg:inline-flex lg:w-full lg:justify-center lg:mb-6 lg:relative lg:items-start",
        type === "mobile-main" &&
          "[display:-webkit-inline-box] w-full justify-left pb-3 overflow-hidden overflow-x-scroll [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden border-gray-2250 border-b-1",
        type === "top-mobile" && "grid gap-5",
        isArticleFirstFoldEnabled && type === "main" && "lg:mt-5 lg:mb-5",
      )}
    >
      {items.map((item: HeaderMenuDetails, index: number) => (
        <ListItem
          key={`${testId}-item-${index}`}
          testId={`${testId}-item-${index}`}
          className={twMerge(
            "font-sans text-gray-200",
            type === "social" && "p-xxs lg:px-2 lg:mx-0 lg:py-1",
            type === "main" && item.url && "inline-grid lg:pt-0 lg:flex",
            type === "top-mobile" && "inline-grid",
            type === "mobile-main" && item.url && "pt-sm px-4",
            type === "main" &&
              item.url === "https://www.tabla.com.sg/" &&
              "lg:flex-shrink-0",
          )}
        >
          {item.child && item.childrens ? (
            <>
              <div ref={dropdownRef}>
                <button
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={dropdown ? "true" : "false"}
                  className="hidden lg:block font-bold text-md lg:text-sm lg:px-3 lg:py-2 hover:text-pink-200 lg:shrink-0 lg:items-center lg:whitespace-nowrap"
                  onClick={toggleDropdown}
                >
                  {dropdown ? (
                    <span className="after:ml-1 ml-1 hover:after:border-y-pink-200 after:inline-block after:content-[''] after:border-solid after:border-x-[.3em] after:border-x-transparent after:border-b-[.3em] after:border-t-0 after:align-[.15em]">
                      {item.title}
                    </span>
                  ) : (
                    <span className="after:ml-1 ml-1 hover:after:border-y-pink-200 after:inline-block after:content-[''] after:border-solid after:border-x-[.3em] after:border-x-transparent after:border-t-[.3em] after:border-b-0 after:align-[.15em]">
                      {item.title}
                    </span>
                  )}
                </button>

                <SubMenuDropdown
                  submenus={item.childrens}
                  dropdown={dropdown}
                  testId={testId}
                  type={type}
                />
              </div>
            </>
          ) : (
            <>
              <Link
                reloadDocument
                key={index}
                to={item.url}
                target={item.newTab ? "_blank" : "_self"}
                rel="noreferrer"
                className={twMerge(
                  "font-bold text-md tracking-[0.5px]",
                  item.images && item.title && "inline-flex",
                  type === "top-mobile" && "text-red-100",
                  type === "main" &&
                    "hover:text-pink-200 lg:text-sm lg:px-2.5 lg:py-2 xl:px-5 xl:py-2.5",
                  isArticleFirstFoldEnabled &&
                    type === "main" &&
                    "lg:leading-410",
                  type === "mobile-main" &&
                    "hover:text-pink-200 max-lg:text-1sm",
                  type === "mobile-main" &&
                    `/${sectionName}` == item.url &&
                    "text-red-100",
                )}
              >
                {item.images ? (
                  <img
                    height={item.height}
                    width={item.width}
                    src={item.images}
                    alt={`${item.alt}-icon`}
                    className={twMerge(
                      "h-5",
                      type === "social" && "xs:h-4.5 lg:h-5",
                      type === "top-mobile" && "h-xm pr-1 mt-0.5 ml-1",
                      type === "main" && "lg:h-auto lg:w-12.5 lg:mt-1",
                      item.url === "https://www.tabla.com.sg/" &&
                        "xs:h-4.5 xs:w-auto lg:!mt-0.5",
                    )}
                  />
                ) : null}
                {item.title}
              </Link>
            </>
          )}
        </ListItem>
      ))}
    </List>
  );
}
